import { supabase } from '../lib/supabase';

function isSchemaError(error) {
  if (!error) return false;
  
  if (error?.code && typeof error?.code === 'string') {
    const errorClass = error?.code?.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') {
      return true;
    }
    if (errorClass === '23') {
      return false;
    }
  }
  
  if (error?.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /invalid.*syntax/i,
      /type.*does not exist/i,
      /undefined.*column/i,
      /undefined.*table/i,
      /undefined.*function/i,
    ];
    
    return schemaErrorPatterns?.some(pattern => pattern?.test(error?.message));
  }
  
  return false;
}

function toSnakeCase(obj) {
  const snakeObj = {};
  for (const key in obj) {
    const snakeKey = key?.replace(/([A-Z])/g, '_$1')?.toLowerCase();
    snakeObj[snakeKey] = obj?.[key];
  }
  return snakeObj;
}

function toCamelCase(obj) {
  if (!obj) return obj;
  const camelObj = {};
  for (const key in obj) {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    camelObj[camelKey] = obj?.[key];
  }
  return camelObj;
}

const contractService = {
  async getByProjectId(projectId) {
    try {
      const { data, error } = await supabase?.from('contracts')?.select(`
          *,
          subcontractors (
            id,
            company_name,
            trade,
            contact_person,
            contact_email,
            contact_phone
          )
        `)?.eq('project_id', projectId)?.order('created_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else if (error?.code === 'PGRST116') {
          return [];
        } else {
          console.log('Data error:', error?.message);
          return [];
        }
      }

      return data?.map(contract => ({
        ...toCamelCase(contract),
        subcontractor: contract?.subcontractors ? toCamelCase(contract?.subcontractors) : null
      })) || [];
    } catch (error) {
      console.error('Error fetching contracts:', error);
      throw error;
    }
  },

  async create(contractData) {
    try {
      const snakeData = toSnakeCase(contractData);
      const { data, error } = await supabase?.from('contracts')?.insert(snakeData)?.select(`
          *,
          subcontractors (
            id,
            company_name,
            trade,
            contact_person,
            contact_email,
            contact_phone
          )
        `)?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          console.log('Data error:', error?.message);
          return null;
        }
      }

      return {
        ...toCamelCase(data),
        subcontractor: data?.subcontractors ? toCamelCase(data?.subcontractors) : null
      };
    } catch (error) {
      console.error('Error creating contract:', error);
      throw error;
    }
  },

  async update(id, contractData) {
    try {
      const snakeData = toSnakeCase(contractData);
      const { data, error } = await supabase?.from('contracts')?.update(snakeData)?.eq('id', id)?.select(`
          *,
          subcontractors (
            id,
            company_name,
            trade,
            contact_person,
            contact_email,
            contact_phone
          )
        `)?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          console.log('Data error:', error?.message);
          return null;
        }
      }

      return {
        ...toCamelCase(data),
        subcontractor: data?.subcontractors ? toCamelCase(data?.subcontractors) : null
      };
    } catch (error) {
      console.error('Error updating contract:', error);
      throw error;
    }
  },

  async delete(id) {
    try {
      const { error } = await supabase?.from('contracts')?.delete()?.eq('id', id);

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          console.log('Data error:', error?.message);
          return false;
        }
      }

      return true;
    } catch (error) {
      console.error('Error deleting contract:', error);
      throw error;
    }
  }
};

export default contractService;